import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export type AppMode = 'cloud' | 'lan';

export interface CloudConfig {
  apiUrl: string;
  installationId: string;
  enabled: boolean;
  connected: boolean;
  apiKeySet: boolean;
}

export interface OtaStatus {
  centralFwVersion: string;
  centralHwVersion: string;
  thermOtaRunning: boolean;
  thermostats: {
    mac: string;
    status: 'idle' | 'sending' | 'done' | 'failed';
    chunksAcked: number;
    totalChunks: number;
    error: number;
  }[];
}

/**
 * Manages the current connection mode (LAN vs Cloud) and
 * provides helpers to build URLs accordingly.
 *
 * In LAN mode: all requests go directly to the ESP32-Central IP.
 * In Cloud mode: all requests go to the Cloud API (apiUrl).
 */
@Injectable({ providedIn: 'root' })
export class ConnectionService {
  private readonly http = inject(HttpClient);

  readonly mode = signal<AppMode>((localStorage.getItem('rf_connection_mode') as AppMode | null) ?? 'cloud');
  readonly lanIp = signal<string>(localStorage.getItem('rf_lan_ip') ?? '');

  setMode(m: AppMode): void {
    this.mode.set(m);
    localStorage.setItem('rf_connection_mode', m);
  }

  setLanIp(ip: string): void {
    this.lanIp.set(ip);
    localStorage.setItem('rf_lan_ip', ip);
  }

  /** Returns the base URL for the current mode */
  baseUrl(): string {
    if (this.mode() === 'lan') {
      return `http://${this.lanIp()}`;
    }
    return environment.apiUrl;
  }

  /** LAN-only: get cloud config from central */
  getLanCloudConfig(lanIp: string) {
    return this.http.get<CloudConfig>(`http://${lanIp}/api/cloud`);
  }

  setLanCloudConfig(lanIp: string, cfg: Partial<CloudConfig> & { apiKey?: string }) {
    return this.http.put<CloudConfig>(`http://${lanIp}/api/cloud`, cfg);
  }
}

@Injectable({ providedIn: 'root' })
export class OtaService {
  private readonly http = inject(HttpClient);
  private readonly conn = inject(ConnectionService);

  /* Cloud OTA */
  getFirmwares() {
    return this.http.get<unknown[]>(`${environment.apiUrl}/ota/firmwares`);
  }

  uploadFirmware(file: File, target: 'central' | 'thermostat', hwVersion: number, fwVersion: string) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('target', target);
    fd.append('hw_version', String(hwVersion));
    fd.append('fw_version', fwVersion);
    return this.http.post(`${environment.apiUrl}/ota/firmwares/upload`, fd);
  }

  queueOta(installationId: string, firmwareId: string) {
    return this.http.post(
      `${environment.apiUrl}/ota/install/${installationId}`,
      { firmware_id: firmwareId },
    );
  }

  /* LAN OTA — direct to central */
  getLanOtaStatus(lanIp: string) {
    return this.http.get<OtaStatus>(`http://${lanIp}/api/ota/status`);
  }

  flashCentralLan(lanIp: string, file: File, version: string) {
    return this.http.post(
      `http://${lanIp}/api/ota/central?version=${encodeURIComponent(version)}`,
      file,
      { headers: { 'Content-Type': 'application/octet-stream' } },
    );
  }

  flashThermostatLan(lanIp: string, file: File, version: string) {
    return this.http.post(
      `http://${lanIp}/api/ota/thermostat?version=${encodeURIComponent(version)}`,
      file,
      { headers: { 'Content-Type': 'application/octet-stream' } },
    );
  }
}
